import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Optional,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef } from '@angular/material/dialog';

type IconColor =
  | 'blue'
  | 'green'
  | 'red'
  | 'purple'
  | 'orange'
  | 'amber'
  | 'gray'
  | 'slate'
  | string;

const ICON_COLOR_BG: Record<string, string> = {
  blue: 'bg-blue-50',
  green: 'bg-green-50',
  red: 'bg-red-50',
  purple: 'bg-purple-50',
  orange: 'bg-orange-50',
  amber: 'bg-amber-50',
  gray: 'bg-gray-100',
  slate: 'bg-slate-100',
};

const ICON_COLOR_FG: Record<string, string> = {
  blue: 'text-blue-600',
  green: 'text-green-600',
  red: 'text-red-600',
  purple: 'text-purple-600',
  orange: 'text-orange-600',
  amber: 'text-amber-600',
  gray: 'text-gray-600',
  slate: 'text-slate-600',
};

@Component({
  selector: 'app-responsive-modal-header',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  // Layout-transparent: the dialog component using the kit owns the flex
  // column that pins header/content/actions. By rendering as `display: contents`
  // the inner header div becomes effectively a direct flex child of that
  // parent. See styles.scss `.responsive-modal` block for the full chain.
  styles: [`:host { display: contents; }`],
  template: `
    <div class="flex items-center justify-between gap-3 p-2 md:p-4 border-b">
      <div class="flex items-center gap-3 min-w-0">
        @if (icon()) {
          <div
            class="flex h-9 w-9 md:h-10 md:w-10 shrink-0 items-center justify-center rounded-lg"
            [class]="iconBgClass()"
          >
            <mat-icon [class]="iconFgClass()">{{ icon() }}</mat-icon>
          </div>
        }
        <div class="flex flex-col min-w-0">
          <h2 class="text-base md:text-lg font-bold truncate">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="text-xs md:text-sm text-gray-500 truncate">{{ subtitle() }}</p>
          }
        </div>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <ng-content select="[header-actions]" />
        @if (showClose()) {
          <button
            mat-icon-button
            type="button"
            class="-mr-2"
            (click)="handleClose()"
            aria-label="Cerrar"
          >
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResponsiveModalHeaderComponent {
  readonly title = input.required<string>();
  readonly subtitle = input<string | undefined>(undefined);
  readonly icon = input<string | undefined>(undefined);
  readonly iconColor = input<IconColor>('blue');
  readonly showClose = input<boolean>(true);
  /** When true (default) and a MatDialogRef is in scope, the close button calls
   * dialogRef.close(closeResult) automatically. Set to false to opt-out and
   * handle close exclusively via the (closed) output. */
  readonly autoCloseDialog = input<boolean>(true);
  /** Result passed to dialogRef.close() when auto-close is enabled. */
  readonly closeResult = input<unknown>(false);

  readonly closed = output<void>();

  private readonly dialogRef = inject<MatDialogRef<unknown> | null>(MatDialogRef, {
    optional: true,
  });

  readonly iconBgClass = computed(() => ICON_COLOR_BG[this.iconColor()] ?? 'bg-blue-50');
  readonly iconFgClass = computed(() => ICON_COLOR_FG[this.iconColor()] ?? 'text-blue-600');

  handleClose(): void {
    this.closed.emit();
    if (this.autoCloseDialog() && this.dialogRef) {
      this.dialogRef.close(this.closeResult());
    }
  }
}
